/* audit-model.js: normalize the audit tape from either backend mode into one
 * shape the Audit view and rail render from. PURE: no DOM, no imports, so the
 * node:test suite imports it directly. Server order is canonical, this module
 * never re-sorts by ts.
 *
 * normalizeAudit(raw, mode, opts) -> { groups, truncated }
 *   group = { id, kind: "task"|"run"|"undo"|"day", title, ts,
 *             checkpoints: [{label, ts, note}], rollup: {reversible, risky, irreversible}, entries: [...] }
 *   entry = { id, ts, tool, family, op, targetLabel, reversibility,
 *             reverse_status, reverse_of, has_diff }
 */

const TARGET_PRIORITY = ["name", "table_name", "rule_id", "field_id", "id", "attribute_id"];

export function shortId(s) {
  return String(s == null ? "" : s).replace(/^promotion:/, "").slice(0, 8);
}

function dayOf(ts) {
  const s = String(ts || "");
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export function parseHostedTargetLabel(family, targetStr) {
  const fam = String(family || "").replace(/_/g, " ");
  const pairs = {};
  let firstVal = "";
  for (const seg of String(targetStr || "").split(";")) {
    if (!seg) continue;
    const eq = seg.indexOf("=");
    if (eq < 0) continue;
    const k = seg.slice(0, eq);
    const v = seg.slice(eq + 1);
    if (!(k in pairs)) pairs[k] = v;
    if (firstVal === "") firstVal = v;
  }
  let ident = "";
  for (const k of TARGET_PRIORITY) {
    if (k in pairs) { ident = pairs[k]; break; }
  }
  if (!ident) ident = firstVal;
  return `${fam} ${ident}`.trim();
}

function rollupOf(entries) {
  const roll = { reversible: 0, risky: 0, irreversible: 0 };
  for (const e of entries) if (e.reversibility in roll) roll[e.reversibility] += 1;
  return roll;
}

function localGroup(g) {
  const gid = String(g.group_id != null ? g.group_id : (g.id || ""));
  let kind, title;
  if (gid.startsWith("promotion:")) { kind = "run"; title = "Run " + shortId(gid); }
  else if (gid.startsWith("session-")) { kind = "day"; title = dayOf(g.ts); }
  else { kind = "task"; title = "Task " + shortId(gid); }
  const entries = (g.entries || []).map((e) => ({
    id: e.id, ts: e.ts, tool: e.tool, family: e.family, op: e.op,
    targetLabel: e.target_label || "", // local: server label, no title join
    reversibility: e.reversibility, reverse_status: e.reverse_status,
    reverse_of: e.reverse_of != null ? e.reverse_of : null,
    has_diff: !!e.has_diff,
  }));
  const checkpoints = (g.checkpoints || []).map((c) =>
    typeof c === "string"
      ? { label: c, ts: null, note: "" }
      : { label: c.label, ts: c.ts != null ? c.ts : null, note: c.note || "" });
  return {
    id: gid, kind, title, ts: g.ts, checkpoints,
    rollup: g.rollup || rollupOf(entries), entries,
  };
}

function convTitle(convId, conversations) {
  const map = conversations || {};
  if (!Object.prototype.hasOwnProperty.call(map, convId)) return "Task " + shortId(convId);
  const t = map[convId];
  return (t == null || t === "") ? "Untitled" : String(t);
}

function hostedGroups(entries, conversations) {
  const order = [];
  const buckets = new Map();
  for (const r of entries) {
    let key;
    if (r.conversation_id != null) key = "conv:" + r.conversation_id; // conversation first
    else if (r.run_id != null) key = "run:" + r.run_id; // run only when conversation is null
    else key = "day:" + dayOf(r.ts);
    if (!buckets.has(key)) { buckets.set(key, []); order.push(key); }
    buckets.get(key).push(r);
  }
  const groups = [];
  for (const key of order) {
    const rows = buckets.get(key);
    const ents = rows.map((r) => ({
      id: r.id, ts: r.ts, tool: r.tool, family: r.family, op: r.op,
      targetLabel: parseHostedTargetLabel(r.family, r.target),
      reversibility: r.reversibility, reverse_status: r.reverse_status,
      reverse_of: r.reverse_of != null ? r.reverse_of : null,
      has_diff: !!r.has_diff,
    }));
    const allReverse = ents.length > 0 && ents.every((e) => e.reverse_of);
    let kind, title, id;
    if (key.startsWith("conv:")) {
      id = key.slice(5);
      if (allReverse) { kind = "undo"; title = "Undo of earlier changes"; }
      else { kind = "task"; title = convTitle(id, conversations); }
    } else if (key.startsWith("run:")) {
      id = key.slice(4);
      if (allReverse) { kind = "undo"; title = "Undo of earlier changes"; }
      else { kind = "run"; title = "Run " + shortId(id); }
    } else {
      id = key.slice(4); kind = "day"; title = id;
    }
    groups.push({
      id, kind, title,
      ts: ents.length ? ents[0].ts : null, // newest first: first row is newest
      checkpoints: [], rollup: rollupOf(ents), entries: ents,
    });
  }
  return groups;
}

export function normalizeAudit(raw, mode, opts = {}) {
  if (mode === "hosted") {
    const entries = (raw && raw.entries) || [];
    const limit = opts.limit != null ? opts.limit : 200;
    return { groups: hostedGroups(entries, opts.conversations), truncated: entries.length === limit };
  }
  const groups = (raw && raw.groups) || [];
  return { groups: groups.map(localGroup), truncated: !!(raw && raw.truncated) };
}
